import { useRouter } from 'vue-router'

import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { type ErrorHandlingOptions } from '@/lib/types'
import { type ErrorState, useErrorStore } from '@/stores/error.stores'

export const useErrorHandler = () => {
  const router = useRouter()
  const errorStore = useErrorStore()

  const handleError = async (options: ErrorHandlingOptions = {}) => {
    const { error, redirectToErrorPage = true, ...restOptions } = options

    const isServerError = (error as unknown as Response)?.status >= 500
    const isNotFoundError = (error as unknown as Response)?.status === 404
    const isGlobalError = isServerError || isNotFoundError
    const errorState: Partial<ErrorState> = { ...restOptions, isGlobal: isGlobalError }

    errorStore.setError(errorState)
    if (redirectToErrorPage) {
      await router.push({ name: 'error' })
    }
    handleErrorMessage(error)
  }

  const handleNetworkError = async () => {
    await handleError({
      title: 'Network Error',
      message:
        'Unable to connect to our servers. Please check your internet connection and try again.',
    })
  }

  const handleServerError = async () => {
    await handleError({
      title: 'Server Error',
      message: 'Our servers are experiencing issues. Please try again in a few moments.',
    })
  }

  const handleAuthError = async () => {
    await handleError({
      title: 'Authentication Error',
      message: 'Your session has expired. Please log in again.',
      showRetryButton: false,
    })
  }

  return {
    handleError,
    handleNetworkError,
    handleServerError,
    handleAuthError,
  }
}
